import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useResultsChat } from '@/hooks/useResultsChat';
import { useLanguage } from '@/contexts/LanguageContext';
import { BehavioralScores, PersonalityProfile } from '@/types/resultsTypes';
import { cn } from '@/lib/utils';

interface ResultsChatProps {
  scores: BehavioralScores;
  isDualAssessment: boolean;
  selfScores?: BehavioralScores | null;
  workScores?: BehavioralScores | null;
  profileType?: string;
}

const suggestedQuestions = {
  en: [
    "What does my profile mean for teamwork?",
    "What careers might suit me?",
    "How do I approach decisions?",
    "What are my key strengths?",
  ],
  pt: [
    "O que meu perfil significa para trabalho em equipe?",
    "Quais carreiras podem me servir?",
    "Como eu abordo decisões?",
    "Quais são meus pontos fortes?",
  ],
  es: [
    "¿Qué significa mi perfil para el trabajo en equipo?",
    "¿Qué carreras me convienen?",
    "¿Cómo abordo las decisiones?",
    "¿Cuáles son mis fortalezas?",
  ],
};

const ResultsChat: React.FC<ResultsChatProps> = ({
  scores,
  isDualAssessment,
  selfScores,
  workScores,
  profileType,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { language } = useLanguage();
  
  const { messages, sendMessage, isLoading, error } = useResultsChat(
    { scores, isDualAssessment, selfScores, workScores, profileType },
    language
  );

  const questions = suggestedQuestions[language as keyof typeof suggestedQuestions] || suggestedQuestions.en;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleSend = () => {
    if (input.trim() && !isLoading) {
      sendMessage(input.trim());
      setInput('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    sendMessage(question);
  };

  const labels = {
    en: {
      title: "Ask about your results",
      placeholder: "Ask anything about your profile...",
      collapse: "Close chat",
      typing: "Thinking...",
    },
    pt: {
      title: "Pergunte sobre seus resultados",
      placeholder: "Pergunte qualquer coisa sobre seu perfil...",
      collapse: "Fechar chat",
      typing: "Pensando...",
    },
    es: {
      title: "Pregunta sobre tus resultados",
      placeholder: "Pregunta cualquier cosa sobre tu perfil...",
      collapse: "Cerrar chat",
      typing: "Pensando...",
    },
  };

  const uiLabels = labels[language as keyof typeof labels] || labels.en;

  if (!isOpen) {
    return (
      <section className="px-4 md:px-8 lg:px-16 py-12">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setIsOpen(true)}
            className="w-full bg-muted/50 hover:bg-muted border border-border rounded-xl p-5 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="text-left">
                <h3 className="text-base font-medium text-foreground">{uiLabels.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'pt' ? 'Converse com a IA sobre seu perfil' :
                   language === 'es' ? 'Habla con la IA sobre tu perfil' :
                   'Chat with AI about your profile'}
                </p>
              </div>
              <MessageCircle className="w-5 h-5 text-muted-foreground" />
            </div>
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="px-4 md:px-8 lg:px-16 py-12">
      <div className="max-w-4xl mx-auto">
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {/* Header */}
          <div className="px-5 py-4 flex items-center justify-between border-b border-border">
            <div>
              <h3 className="font-medium text-foreground">Zentrix AI</h3>
              <p className="text-xs text-muted-foreground">{uiLabels.title}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Messages Area */}
          <div className="h-[400px] overflow-y-auto p-4 space-y-4 bg-background/50">
            {messages.length === 0 ? (
              <div className="space-y-4">
                <p className="text-center text-muted-foreground text-sm py-4">
                  {language === 'pt' ? 'Escolha uma pergunta ou escreva a sua:' :
                   language === 'es' ? 'Elige una pregunta o escribe la tuya:' :
                   'Choose a question or write your own:'}
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {questions.map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSuggestedQuestion(question)}
                      className="px-4 py-2 text-sm bg-primary/5 hover:bg-primary/10 text-foreground rounded-full border border-primary/20 transition-colors"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={cn(
                    "flex",
                    msg.role === 'user' ? "justify-end" : "justify-start"
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[80%] rounded-2xl px-4 py-3 text-sm",
                      msg.role === 'user'
                        ? "bg-primary text-primary-foreground rounded-br-md"
                        : "bg-muted text-foreground rounded-bl-md"
                    )}
                  >
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))
            )}
            
            {isLoading && messages[messages.length - 1]?.role === 'user' && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span>{uiLabels.typing}</span>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="text-center text-destructive text-sm py-2">
                {error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-border bg-card">
            <div className="flex gap-2 w-full">
              <div className="flex-1 min-w-0">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={uiLabels.placeholder}
                  disabled={isLoading}
                  className="w-full"
                />
              </div>
              <Button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                size="icon"
                className="shrink-0"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResultsChat;
